/**
 * Photo Service - Firebase Storage photo upload for job checkpoints
 *
 * Handles:
 * - Photo upload to Firebase Storage with progress tracking
 * - Image compression before upload
 * - GPS + timestamp metadata tagging
 * - Download URL retrieval
 * - Photo deletion
 *
 * Storage path: photos/{tenantId}/{jobId}/{checkpointId}/{timestamp}_{filename}
 *
 * Based on the upload pattern in design-import.service.ts
 */

import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, storage } from '@/firebase';
import { getTenantIdSync } from '@/lib/tenant';
import type { PhotoMetadata, JobPhoto } from '@/types/schema';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Max file size: 10 MB */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Compression target: 1200px max dimension */
const MAX_IMAGE_DIMENSION = 1200;

/** Thumbnail max dimension: 200px */
const THUMBNAIL_DIMENSION = 200;

/** JPEG compression quality (0-1) */
const COMPRESSION_QUALITY = 0.8;

/** Thumbnail JPEG quality */
const THUMBNAIL_QUALITY = 0.5;


// ============================================================================
// VALIDATION
// ============================================================================

export interface PhotoValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate an image file before upload.
 */
export function validatePhotoFile(file: File): PhotoValidationResult {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file type (allow any image/* type for camera captures)
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { valid: false, error: `File too large (${sizeMB} MB). Maximum is 10 MB.` };
  }

  return { valid: true };
}

// ============================================================================
// IMAGE COMPRESSION
// ============================================================================

/**
 * Compress an image file by resizing and reducing quality.
 * Returns a Blob ready for upload. `outputType` defaults to JPEG; pass
 * 'image/png' to keep transparency (a JPEG turns transparent pixels black).
 */
export function compressImage(
  file: File,
  maxDimension: number = MAX_IMAGE_DIMENSION,
  quality: number = COMPRESSION_QUALITY,
  outputType: 'image/jpeg' | 'image/png' = 'image/jpeg'
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down if exceeds max dimension
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Generate a thumbnail blob from an image file.
 */
export function generateThumbnail(file: File): Promise<Blob> {
  return compressImage(file, THUMBNAIL_DIMENSION, THUMBNAIL_QUALITY);
}

// ============================================================================
// GPS CAPTURE
// ============================================================================

export interface GPSCoordinates {
  lat: number;
  lng: number;
}

/**
 * Get current GPS position.
 * Returns null if geolocation is unavailable or permission denied.
 */
export function getCurrentPosition(): Promise<GPSCoordinates | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        // Permission denied or error - return null (non-blocking)
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 30000,
      }
    );
  });
}

// ============================================================================
// STORAGE PATH HELPERS
// ============================================================================

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 100);
}

function getPhotoStoragePath(
  tenantId: string,
  jobId: string,
  checkpointId: string,
  filename: string
): string {
  const timestamp = Date.now();
  const sanitized = sanitizeFilename(filename);
  return `photos/${tenantId}/${jobId}/${checkpointId}/${timestamp}_${sanitized}`;
}

function getThumbnailStoragePath(photoPath: string): string {
  // Insert _thumb before the file extension
  const lastDot = photoPath.lastIndexOf('.');
  if (lastDot === -1) return `${photoPath}_thumb`;
  return `${photoPath.substring(0, lastDot)}_thumb${photoPath.substring(lastDot)}`;
}

// ============================================================================
// UPLOAD
// ============================================================================

export interface PhotoUploadResult {
  photoMetadata: PhotoMetadata;
}

/**
 * Upload a photo for a job checkpoint.
 *
 * Flow:
 * 1. Validate file
 * 2. Compress image
 * 3. Generate thumbnail
 * 4. Capture GPS coordinates
 * 5. Upload both to Firebase Storage
 * 6. Return PhotoMetadata
 *
 * @param file - The image file from camera or file picker
 * @param jobId - The job this photo belongs to
 * @param checkpointId - The checkpoint this photo is for
 * @param onProgress - Optional progress callback (0-100)
 */
export async function uploadCheckpointPhoto(
  file: File,
  jobId: string,
  checkpointId: string,
  onProgress?: (percent: number) => void
): Promise<PhotoUploadResult> {
  // 1. Validate
  const validation = validatePhotoFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const tenantId = getTenantIdSync();
  const timestamp = new Date().toISOString();

  // 2-4. Compress, generate thumbnail, and capture GPS in parallel
  const [compressedBlob, thumbnailBlob, gps] = await Promise.all([
    compressImage(file),
    generateThumbnail(file),
    getCurrentPosition(),
  ]);

  // 5. Upload main photo
  const photoPath = getPhotoStoragePath(tenantId, jobId, checkpointId, file.name || 'photo.jpg');
  const photoRef = ref(storage, photoPath);

  const uploadTask = uploadBytesResumable(photoRef, compressedBlob, {
    contentType: 'image/jpeg',
    customMetadata: {
      tenantId,
      jobId,
      checkpointId,
      originalName: file.name,
      uploadedAt: timestamp,
      ...(gps ? { lat: String(gps.lat), lng: String(gps.lng) } : {}),
    },
  });

  // Track upload progress
  const downloadURL = await new Promise<string>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        // Main photo is 80% of total progress
        onProgress?.(Math.round(percent * 0.8));
      },
      (error) => {
        console.error('[photo] Upload failed:', error);
        reject(error);
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        } catch (error) {
          reject(error);
        }
      }
    );
  });

  // 6. Upload thumbnail
  let thumbnailUrl: string | undefined;
  try {
    const thumbPath = getThumbnailStoragePath(photoPath);
    const thumbRef = ref(storage, thumbPath);
    const thumbTask = uploadBytesResumable(thumbRef, thumbnailBlob, {
      contentType: 'image/jpeg',
      customMetadata: { tenantId, jobId, checkpointId, type: 'thumbnail' },
    });

    thumbnailUrl = await new Promise<string>((resolve, reject) => {
      thumbTask.on(
        'state_changed',
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          // Thumbnail is remaining 20%
          onProgress?.(80 + Math.round(percent * 0.2));
        },
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(thumbTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    // Thumbnail failure is non-critical, continue without it
    console.warn('[photo] Thumbnail upload failed:', error);
  }

  onProgress?.(100);

  // 7. Build PhotoMetadata
  const photoMetadata: PhotoMetadata = {
    url: downloadURL,
    storagePath: photoPath,
    timestamp,
    ...(gps ? { lat: gps.lat, lng: gps.lng } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
  };

  return { photoMetadata };
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a photo and its thumbnail from Firebase Storage.
 */
export async function deleteCheckpointPhoto(storagePath: string): Promise<void> {
  try {
    // Delete main photo
    const photoRef = ref(storage, storagePath);
    await deleteObject(photoRef);

    // Try to delete thumbnail too
    try {
      const thumbPath = getThumbnailStoragePath(storagePath);
      const thumbRef = ref(storage, thumbPath);
      await deleteObject(thumbRef);
    } catch {
      // Thumbnail may not exist, ignore
    }
  } catch (error) {
    console.error('[photo] Delete failed:', error);
    throw error;
  }
}

// ============================================================================
// JOB-LEVEL PHOTO CAPTURE (on-site tech photos)
// ============================================================================

/**
 * Upload a photo to Firebase Storage and add reference to job document.
 * Used by PhotoCaptureModule for on-site photo capture.
 *
 * @param jobId - The job document ID
 * @param file - The image File from input[type=file]
 * @param techId - ID of the tech uploading
 * @param techName - Name of the tech (denormalized)
 * @param category - Optional photo category
 * @param caption - Optional caption
 * @returns The created JobPhoto object
 */
export async function uploadJobPhoto(
  jobId: string,
  file: File,
  techId: string,
  techName?: string,
  caption?: string,
  category?: JobPhoto['category']
): Promise<JobPhoto> {
  const timestamp = Date.now();
  const sanitized = sanitizeFilename(file.name || 'photo.jpg');
  const storagePath = `jobs/${jobId}/photos/${timestamp}_${sanitized}`;

  const storageRef = ref(storage, storagePath);

  // Compress if image is very large (> 2MB)
  let uploadBlob: Blob = file;
  if (file.size > 2 * 1024 * 1024 && file.type.startsWith('image/')) {
    try {
      uploadBlob = await compressImage(file, MAX_IMAGE_DIMENSION, COMPRESSION_QUALITY);
    } catch {
      // Fall back to original if compression fails
      uploadBlob = file;
    }
  }

  await uploadBytes(storageRef, uploadBlob, {
    contentType: file.type || 'image/jpeg',
    customMetadata: {
      jobId,
      techId,
      uploadedAt: new Date().toISOString(),
    },
  });

  const url = await getDownloadURL(storageRef);

  // Build photo object — Firestore arrayUnion rejects undefined values,
  // so only include optional fields when they have a real value.
  const photo: JobPhoto = {
    id: crypto.randomUUID(),
    url,
    storagePath,
    timestamp: new Date().toISOString(),
    techId,
  };
  if (techName) photo.techName = techName;
  if (caption) photo.caption = caption;
  if (category) photo.category = category;

  // Add to job document
  await updateDoc(doc(db, 'jobs', jobId), {
    photos: arrayUnion(photo),
    updatedAt: new Date().toISOString(),
  });

  return photo;
}

/**
 * Delete a job photo from Firebase Storage and remove reference from job document.
 */
export async function deleteJobPhoto(
  jobId: string,
  photo: JobPhoto
): Promise<void> {
  // Delete from Storage
  try {
    const storageRef = ref(storage, photo.storagePath);
    await deleteObject(storageRef);
  } catch (err) {
    // Storage file may already be deleted — continue to remove reference
    console.warn('Could not delete storage file:', err);
  }

  // Remove from job document
  await updateDoc(doc(db, 'jobs', jobId), {
    photos: arrayRemove(photo),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Get photo count for a job (useful for badges/counters)
 */
export function getPhotoCount(photos?: JobPhoto[]): number {
  return photos?.length ?? 0;
}
